import React from "react"
import propTypes from "prop-types"
import { Card, CardHeader, CardBody, Row, Col, FormGroup, Label, Input } from "reactstrap"
import AsyncSelect from "react-select/async"
import Select from "react-select"
import paymentForms from "../data/paymentForms.json"
import paymentMethods from "../data/paymentMethods.json"

const ReceiverSection = ({ 
    values, 
    errors, 
    touched, 
    handleChange, 
    handleBlur, 
    setFieldValue,
    handleClientChange, 
    loadCustomerOptions,
    usoCfdiOptions,
}) => {
    const selectedClient = values.client ? { value: values.client, label: values.clientName || values.client } : null
    const selectedUsoCfdi = usoCfdiOptions.find((option) => option.value === values.usoCfdi) || null

    return (
        <Card className="mb-3">
            <CardHeader className="py-2">
                <h5 className="mb-0">Receptor:</h5>
            </CardHeader>
            <CardBody className="p-3">
                <Row className="mb-3">
                    <Col md="12">
                        <FormGroup>
                            <Label for="client">Cliente:</Label>
                            <AsyncSelect
                                value={selectedClient}
                                onChange={(selectedOption) => handleClientChange({
                                    target: {
                                        name: "client",
                                        value: selectedOption ? selectedOption.value : "",
                                        label: selectedOption ? selectedOption.label : ""
                                    }
                                })}
                                onBlur={handleBlur}
                                loadOptions={loadCustomerOptions}
                                defaultOptions
                                isClearable={true}
                                placeholder="Busca por nombre de cliente..."
                                noOptionsMessage={() => "No se encontraron clientes"}
                                loadingMessage={() => "Buscando..."}
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        minHeight: "38px",
                                        borderColor: touched.client && errors.client ? "#dc3545" : base.borderColor,
                                        "&:hover": {
                                            borderColor: touched.client && errors.client ? "#dc3545" : base.borderColor
                                        }
                                    })
                                }}
                            />
                            {touched.client && errors.client && (
                                <div className="text-danger small mt-1">{errors.client}</div>
                            )}
                        </FormGroup>
                    </Col>
                </Row>
                {/* Sección para capturar los datos del receptor de la factura */}
                <Row className="mb-3">
                    <Col md="4">
                        <FormGroup>
                            <Label for="date">Fecha: *</Label>
                            <Input 
                                type="text" 
                                name="date" 
                                value={new Date().toLocaleDateString("es-MX")} 
                                disabled 
                            />
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label for="rfc">RFC: *</Label>
                            <Input 
                                type="text" 
                                name="rfc" 
                                value={values.rfc} 
                                disabled 
                                invalid={touched.rfc && errors.rfc} 
                            />
                            {touched.rfc && errors.rfc && (
                                <div className="invalid-feedback">
                                    {errors.rfc}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label for="paymentMethod">Método de pago: *</Label>
                            <Input 
                                type="select" 
                                name="paymentMethod" 
                                value={values.paymentMethod} 
                                onChange={handleChange} 
                                onBlur={handleBlur} 
                                invalid={touched.paymentMethod && errors.paymentMethod}
                            >
                                {paymentMethods.map(m => (
                                    <option key={m.value} value={m.value}>
                                        {m.label}
                                    </option>
                                ))}
                            </Input>
                            {touched.paymentMethod && errors.paymentMethod && (
                                <div className="invalid-feedback">
                                    {errors.paymentMethod}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                </Row>
                
                <Row className="mb-3">
                    <Col md="4">
                        <FormGroup>
                            <Label for="fiscalRegimen">Régimen Fiscal: *</Label>
                            <Input 
                                type="text" 
                                name="fiscalRegimen" 
                                value={values.fiscalRegimen} 
                                disabled 
                                invalid={touched.fiscalRegimen && errors.fiscalRegimen} 
                            />
                            {touched.fiscalRegimen && errors.fiscalRegimen && (
                                <div className="invalid-feedback">
                                    {errors.fiscalRegimen}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label for="companyName">Razón social: *</Label>
                            <Input 
                                type="text" 
                                name="companyName" 
                                value={values.companyName} 
                                disabled 
                                invalid={touched.companyName && errors.companyName} 
                            />
                            {touched.companyName && errors.companyName && (
                                <div className="invalid-feedback">
                                    {errors.companyName}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label for="paymentForm">Forma de pago: *</Label>
                            <Input 
                                type="select" 
                                name="paymentForm" 
                                value={values.paymentForm} 
                                onChange={handleChange} 
                                onBlur={handleBlur} 
                                invalid={touched.paymentForm && errors.paymentForm}
                            >
                                {paymentForms.map(f => (
                                    <option key={f.value} value={f.value}>
                                        {f.label}
                                    </option>
                                ))}
                            </Input>
                            {touched.paymentForm && errors.paymentForm && (
                                <div className="invalid-feedback">
                                    {errors.paymentForm}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                </Row>
                
                <Row>
                    <Col md="4">
                        <FormGroup>
                            <Label for="postalCode">Código postal: *</Label>
                            <Input 
                                type="text" 
                                name="postalCode" 
                                value={values.postalCode} 
                                disabled 
                                invalid={touched.postalCode && errors.postalCode} 
                            />
                            {touched.postalCode && errors.postalCode && (
                                <div className="invalid-feedback">
                                    {errors.postalCode}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md="4">
                        <FormGroup>
                            <Label for="usoCfdi">UsoCFDI: *</Label>
                            <Select
                                inputId="usoCfdi"
                                name="usoCfdi" 
                                value={selectedUsoCfdi}
                                onChange={(selectedOption) =>
                                    setFieldValue("usoCfdi", selectedOption ? selectedOption.value : "")
                                }
                                onBlur={() => {
                                    handleBlur({
                                        target: {
                                            name: "usoCfdi",
                                        },
                                    })
                                }}
                                options={usoCfdiOptions}
                                isClearable={true}
                                placeholder="Selecciona un uso CFDI"
                                noOptionsMessage={() => "No se encontraron opciones"}
                                styles={{
                                    control: (base, state) => ({
                                        ...base,
                                        minHeight: "38px",
                                        borderColor: touched.usoCfdi && errors.usoCfdi ? "#dc3545" : base.borderColor,
                                        "&:hover": {
                                        borderColor: touched.usoCfdi && errors.usoCfdi ? "#dc3545" : base.borderColor,
                                        }
                                    })
                                }}
                            />
                            {touched.usoCfdi && errors.usoCfdi && (
                                <div className="text-danger small mt-1">
                                    {errors.usoCfdi}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    )
}

ReceiverSection.propTypes = {
    values: propTypes.object.isRequired,
    errors: propTypes.object.isRequired,
    touched: propTypes.object.isRequired,
    handleChange: propTypes.func.isRequired,
    handleBlur: propTypes.func.isRequired,
    setFieldValue: propTypes.func.isRequired,
    handleClientChange: propTypes.func.isRequired,
    loadCustomerOptions: propTypes.func.isRequired,
    usoCfdiOptions: propTypes.array.isRequired,
}

export default ReceiverSection
